<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="lg" :visible="modal.show" :title="modalTitle" @show="onShow" @hidden="onHidden"
                   :content-in-form="false" :show-confirmation="showConfirmation" :ok-title="$t('Submit')"
                   :data="model" @ok.prevent="handleSubmit(onSubmit)" scrollable :is-loading="loading"
                   :modal-loading="modalLoading" :disable-submit-btn="modalLoading"
                   modal-class="mobile-full-screen">
      <div class="row mb-3">
        <div class="col-sm-4">
          <customer-select-input-widget attribute="customer_uuid" :model="model" @select="onCustomerChange"
                                        @unselect="onCustomerUnselect" :expand="'billing_address.country'"
                                        :disabled="!!model.uuid"/>
        </div>
        <div class="col-sm-4">
          <input-widget :model="model" attribute="date" type="datepicker" :min="$getMonthAgo(model.created_at)"
                        :max="$getMonthCurrent(model.created_at)"
                        :rules="`min_date:${$getMonthAgo(model.created_at)}|max_date:${$getMonthCurrent(model.created_at)}|required`"/>
        </div>
        <div class="col-sm-4" v-if="!modalSale && isAuction">
          <sale-select-input-widget attribute="auction_uuid" :model="model" @select="onSaleChange"
                                    @unselect="onSaleUnselect"
                                    :disabled="!!model.uuid"/>
        </div>
        <div class="col-md-4" v-if="hasMoreThanOneLanguage">
          <language-code-select-input-widget hint :model="model" attribute="preferred_language_code"/>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4">
          <div class="card">
            <div class="card-header d-flex align-items-center p-2">
              <div>
                {{ $t('Customer Info') }}
              </div>
            </div>
            <div class="card-body p-2" style="min-height: 5.3em">
              <div v-if="currentCustomer">
                <customer-email-address-info :customer="currentCustomer"
                                             show-intra-community-vat-number :show-address="false"/>
              </div>
              <no-data-available v-else v-show="true" height="4"/>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card">
            <div class="card-header d-flex align-items-center p-2">
              <div>
                {{ $t('Settlement Billing address') }}
              </div>
              <b-button class="ml-auto p-0" variant="link" v-permission="'change_settlement'"
                        @click="onBillingAddressEditClick">
                <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                {{ $t('Edit') }}
              </b-button>
            </div>
            <div class="card-body p-2" style="min-height: 5.3em">
              <div v-if="model.settlement_billing_address">
                <p v-html="$options.filters.asAddress(model.settlement_billing_address, false)" class="p-0"></p>
              </div>
              <no-data-available v-else v-show="true" height="4"/>
            </div>
          </div>
        </div>
      </div>


      <div class="settlement-wrapper">
        <div class="row">
          <div class="col-md-12 d-flex justify-content-between">
            <div class="d-flex align-items-center mb-2">
              <h5 class="mb-0">{{ $t('Unsettled Items') }}</h5>
            </div>
            <b-button size="sm" variant="primary" :disabled="selectedItems.size < 1" class="mb-2"
                      @click="moveSelectedToSettled()">
              <font-awesome-icon icon="arrow-down"/>
              {{ $t('Move To Settled') }}
            </b-button>
          </div>
          <div class="col-md-12">
            <table class="unsettled-items-table info-table table table-sm lots-table">
              <tr>
                <th>
                  <b-form-checkbox style="padding-left: 2.01em" @change="selectAllRows($event)"
                                   v-model="isSelectedAll"/>
                </th>
                <th class="lot-col">{{ $t('Lot') }}</th>
                <th class="receipt-col">{{ $t('Receipt / Item') }}</th>
                <th class="hammer-price-col">{{ $t('Hammer') }}</th>
                <th class="hammer-price-col">{{ $t('Commission') }}</th>
                <th class="hammer-adjustment-col">{{ $t('VAT') }}</th>
                <th class="commission-col">{{ $t('Taxable') }}</th>
                <th class="invoice-col">{{ $t('On Hold') }}</th>
              </tr>
              <template v-if="model.unsettled_introductory_financial_items.length > 0">
                <tr :key="`unsettled-item-${item.uuid}`"
                    v-for="(item, index) in model.unsettled_introductory_financial_items">
                  <td>
                    <b-form-checkbox style="padding-left: 2.01em"
                                     @change="onRowSelect($event, item)"
                                     :checked="selectedItems.has(item)"/>
                  </td>
                  <td>
                    <span v-if="item.lot">{{ item.lot.lot_no }}</span>
                  </td>
                  <td>
                    <span v-if="item.lot && item.lot.inventory">{{ item.lot.inventory.stock_no }}</span>
                    <span v-if="item.inventory">{{ item.inventory.stock_no }}</span>
                  </td>
                  <td>
                    <div style="width: 100px">
                      {{ item.hammer_price | asCurrency(currency) }}
                    </div>
                  </td>
                  <td>
                    <div style="width: 100px">
                      <currency-input-widget :currency="currency" size="sm" :model="item" attribute="introductory_commission"
                                             :label="false" @input="onCommissionChange(item)"/>
                    </div>
                  </td>
                  <td>
                    <div style="width: 100px">
                      <currency-input-widget :currency="currency" size="sm" :model="item" attribute="introductory_commission_vat"
                                             :label="false" disabled/>
                    </div>
                  </td>
                  <td>
                    <div style="width: 100px">
                      <input-widget :model="item" size="sm" attribute="introductory_commission_taxable" :label="false"
                                    type="checkbox" @change="onTaxableChange($event, item)"/>
                    </div>
                  </td>
                  <td>
                    <input-widget :model="item" attribute="on_hold" type="checkbox" size="lg" :label="false"/>
                  </td>
                </tr>
              </template>
              <tr v-else>
                <td colspan="9">
                  <no-data-available :height="3"/>
                </td>
              </tr>
            </table>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12 d-flex justify-content-between">
            <div class="d-flex align-items-center mb-2">
              <h5 class="mb-0">{{ $t('Settled Items') }}</h5>
            </div>
            <b-button size="sm" variant="primary" :disabled="selectedSettledItems.size < 1" class="mb-2"
                      @click="moveSelectedToUnsettled()">
              <font-awesome-icon icon="arrow-up"/>
              {{ $t('Move To Unsettled') }}
            </b-button>
          </div>
          <div class="col-md-12">
            <table class="settled-items-table info-table table table-sm lots-table">
              <tr>
                <th>
                  <b-form-checkbox style="padding-left: 2.01em"
                                   @change="selectAllSettledRows($event)"
                                   v-model="isAllSettledSelected"/>
                </th>
                <th class="lot-col">{{ $t('Lot') }}</th>
                <th class="receipt-col">{{ $t('Receipt / Item') }}</th>
                <th class="hammer-price-col">{{ $t('Hammer') }}</th>
                <th class="hammer-price-col">{{ $t('Commission') }}</th>
                <th class="hammer-adjustment-col">{{ $t('VAT') }}</th>
                <th class="commission-col">{{ $t('Taxable') }}</th>
                <th class="invoice-col">{{ $t('On Hold') }}</th>
              </tr>
              <template v-if="model.settled_introductory_financial_items.length > 0">
                <tr :key="`settled-item-${item.uuid}`"
                    v-for="(item, index) in model.settled_introductory_financial_items">
                  <td>
                    <b-form-checkbox style="padding-left: 2.01em"
                                     @change="onSettledRowSelect($event, item)"
                                     :checked="selectedSettledItems.has(item)"/>
                  </td>
                  <td>
                    <span v-if="item.lot">{{ item.lot.lot_no }}</span>
                  </td>
                  <td>
                    <span v-if="item.lot && item.lot.inventory">{{ item.lot.inventory.stock_no }}</span>
                    <span v-if="item.inventory">{{ item.inventory.stock_no }}</span>
                  </td>
                  <td>
                    <div style="width: 100px">
                      {{ item.hammer_price | asCurrency(currency) }}
                    </div>
                  </td>
                  <td>
                    <div style="width: 100px">
                      <currency-input-widget :currency="currency" size="sm" :model="item" attribute="introductory_commission"
                                             :label="false" @input="onCommissionChange(item)"/>
                    </div>
                  </td>
                  <td>
                    <div style="width: 100px">
                      <currency-input-widget :currency="currency" size="sm" :model="item" attribute="introductory_commission_vat"
                                             :label="false" disabled/>
                    </div>
                  </td>
                  <td>
                    <div style="width: 100px">
                      <input-widget :model="item" size="sm" attribute="introductory_commission_taxable" :label="false"
                                    type="checkbox" @change="onTaxableChange($event, item)"/>
                    </div>
                  </td>
                  <td>
                    <input-widget :model="item" attribute="on_hold" type="checkbox" size="lg" :label="false"/>
                  </td>
                </tr>
              </template>
              <tr v-else>
                <td colspan="9">
                  <no-data-available :height="3"/>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>

      <div class="row mt-2">
        <div class="col-md-5 offset-7">
          <settlement-totals-table :model="model" :financial-items="model.settled_introductory_financial_items || []"
                                   :charges="[]" :hide-introductory-commission="false" hide-charge :currency="currency"
                                   hide-lot-handling-charge hide-lot-handling-vat hide-insurance-fee hide-insurance-vat
                                   hide-hammer hide-hammer-adjustment hide-hammer-adjustment-vat hide-hammer-vat
                                   hide-commission hide-commission-vat hide-shipping hide-shipping-vat
                                   :hide-introductory-commission-vat="false" settlement-type="auction_introductory"/>
        </div>
      </div>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "@/core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import NoDataAvailable from "@/core/components/common/NoDataAvailable";
  import SettlementService from "@/modules/settlements/SettlementService";
  import {SOURCE_SETTLEMENTS_TABLE} from "@/modules/constants";
  import CurrencyInputWidget from "@/core/widgets/CurrencyInputWidget";
  import _ from 'lodash';
  import FinancialItemModel from "@/modules/invoices/models/FinancialItem.model";
  import LanguageCodeSelectInputWidget from "@/core/widgets/LanguageCodeSelectInputWidget";
  import CustomerEmailAddressInfo from "../../core/components/customer-info/CustomerEmailAddressInfo";
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import IntroductorySettlementModel from "@/modules/settlements/IntroductorySettlement.model";
  import CustomerSelectInputWidget from "@/core/widgets/CustomerSelectInputWidget";
  import SaleSelectInputWidget from "@/core/widgets/SaleSelectInputWidget";
  import {SettlementModalMixin} from "@/modules/settlements/settlementModal.mixin";
  import uuidV4 from 'uuid';
  import SettlementTotalsTable from "@/modules/settlements/SettlementTotalsTable.vue";

  const {mapState, mapActions} = createNamespacedHelpers('settlements');
  const {mapState: globalMapState, mapActions: globalMapActions} = createNamespacedHelpers('global');

  export default {
    name: "IntroductorySettlementModal",
    mixins: [SettlementModalMixin],
    components: {
      SettlementTotalsTable,
      SaleSelectInputWidget,
      CustomerSelectInputWidget,
      InputWidget,
      LanguageCodeSelectInputWidget,
      CustomerEmailAddressInfo,
      CurrencyInputWidget,
      NoDataAvailable,
      ArtisioModal,
    },
    data() {
      return {
        modalUuid: uuidV4(),
        loading: false,
        showConfirmation: true,
        model: new IntroductorySettlementModel(),
        selectedItems: new Set(),
        isSelectedAll: false,
        selectedSettledItems: new Set(),
        isAllSettledSelected: false,
        isRecalculating: false,
        currentCustomer: null,
        currentSale: null,
        onHoldInitial: {},
      }
    },
    watch: {
      editObject() {
        if (this.editObject && this.editObject.uuid) {
          this.model = new IntroductorySettlementModel(this.editObject)
          this.currentCustomer = _.cloneDeep(this.editObject.customer)
          this.model.unsettled_introductory_financial_items = this.editObject.introductory_financial_items
            .filter(i => i.introductory_commission > 0 && !i.introductory_settlement_uuid)
            .map(i => {
              let model = new FinancialItemModel(i)
              this.calcTaxForItem(model)
              return model
            });
          this.model.settled_introductory_financial_items = this.editObject.introductory_financial_items
            .filter(i => i.introductory_settlement_uuid)
            .map(i => new FinancialItemModel(i));
          const items = [...this.model.settled_introductory_financial_items, ...this.model.unsettled_introductory_financial_items]
          items.forEach(item => {
            this.onHoldInitial[item.uuid] = item.on_hold
          })

          this.customerUuid = this.currentCustomer.uuid
          this.setDefaultAddresses(
            this.editObject.settlement_billing_address,
            null
          )
        }
      },
    },
    computed: {
      ...globalMapState({
        hasMoreThanOneLanguage: state => state.hasMoreThanOneLanguage,
        isSalesTaxEnabled: state => state.isSalesTaxEnabled,
        currentBranch: state => state.currentBranch,
      }),
      ...mapState({
        modal: state => state.introductorySettlementModal,
        modalLoading: state => state.introductorySettlementModal.loading,
        editObject: state => state.introductorySettlementModal.object,
        settlementType: state => state.introductorySettlementModal.type,
        modalSale: state => state.introductorySettlementModal.sale,
      }),
      computedSale() {
        if (this.modalSale) {
          return this.modalSale
        } else if (this.currentSale) {
          return this.currentSale
        }
        return null
      },
      isManual() {
        return this.settlementType && this.settlementType === 'manual_introductory';
      },
      isAuction() {
        return this.settlementType && this.settlementType === 'auction_introductory';
      },
      modalTitle() {
        if (this.modal.debitNoteWithoutRefundParams) {
          return this.$i18n.t('Recreate Settlement {no}', {no: this.model.settlement_no})
        }
        if (this.model.uuid) {
          return this.$i18n.t('Update Settlement');
        }

        if (this.isManual) {
          return this.$i18n.t("Create Manual Introductory Settlement");
        } else if (this.isAuction) {
          return this.$i18n.t("Create Auction Introductory Settlement");
        }
        return this.$i18n.t('Create Introductory Settlement');
      },
      disabled() {
        return !!this.model.uuid;
      },
      currency() {
        if (this.model && this.model.currency) {
          return this.model.currency.symbol
        }
        if (this.computedSale && this.computedSale.currency) {
          return this.computedSale.symbol;
        }
        return null;
      },
    },
    methods: {
      ...mapActions([
        'hideIntroductorySettlementModal',
        'getTableData',
        'getModalData',
        'updateTableSource',
        'resetSearchModel',
        'updateSearchModel'
      ]),
      ...globalMapActions(['showAddressModal']),
      async onShow() {
        this.showConfirmation = true;
      },
      onHidden() {
        this.hideIntroductorySettlementModal();
        this.clearData();
        this.model = new IntroductorySettlementModel();
        this.customerUuid = null
      },
      onMoveToSettledClick(item) {
        this.model.settled_introductory_financial_items.push(_.cloneDeep(item));
        this.$nextTick(function () {
          this.model.unsettled_introductory_financial_items = this.model.unsettled_introductory_financial_items.filter(i => item.uuid !== i.uuid)
          this.isSelectedAll = this.selectedItems.size === this.model.unsettled_introductory_financial_items.length;
          if (!this.model.unsettled_introductory_financial_items.length) {
            this.isSelectedAll = false;
          }
        })
        this.selectedItems.delete(item)
        this.isAllSettledSelected = this.selectedSettledItems.size === this.model.settled_introductory_financial_items.length;
      },
      onMoveToUnsettledClick(item) {
        this.model.unsettled_introductory_financial_items.push(_.cloneDeep(item));
        this.$nextTick(function () {
          this.model.settled_introductory_financial_items = this.model.settled_introductory_financial_items.filter(i => item.uuid !== i.uuid)
          this.isAllSettledSelected = this.selectedSettledItems.size === this.model.settled_introductory_financial_items.length;
          if (!this.model.settled_introductory_financial_items.length) {
            this.isAllSettledSelected = false;
          }
        })
        this.selectedSettledItems.delete(item)
        this.isSelectedAll = this.selectedItems.size === this.model.unsettled_introductory_financial_items.length;
      },
      moveSelectedToSettled() {
        let selectedItemsArray = [...this.selectedItems];
        selectedItemsArray.map(i => {
          this.model.settled_introductory_financial_items.push(_.cloneDeep(i));
          this.$nextTick(() => this.model.unsettled_introductory_financial_items = this.model.unsettled_introductory_financial_items.filter(u => i.uuid !== u.uuid))
        })
        this.selectedItems = new Set();
        this.isSelectedAll = this.selectedItems.size === this.model.unsettled_introductory_financial_items.length;
        this.isAllSettledSelected = this.selectedSettledItems.size === this.model.settled_introductory_financial_items.length;
      },
      moveSelectedToUnsettled() {
        let selectedSettledItemsArray = [...this.selectedSettledItems];
        selectedSettledItemsArray.map(i => {
          this.model.unsettled_introductory_financial_items.push(_.cloneDeep(i));
          this.$nextTick(() => this.model.settled_introductory_financial_items = this.model.settled_introductory_financial_items.filter(u => i.uuid !== u.uuid))
        })
        this.selectedSettledItems = new Set();
        this.isAllSettledSelected = this.selectedSettledItems.size === this.model.settled_introductory_financial_items.length;
        this.isSelectedAll = this.selectedItems.size === this.model.unsettled_introductory_financial_items.length;
      },
      onRowSelect(checked, item) {
        if (checked) {
          this.selectedItems.add(item)
        } else {
          this.selectedItems.delete(item)
        }
        this.isSelectedAll = this.selectedItems.size === this.model.unsettled_introductory_financial_items.length;
        this.selectedItems = new Set(this.selectedItems);
      },
      selectAllRows(checked) {
        this.clearSelected();
        this.isSelectedAll = checked;

        if (this.isSelectedAll) {
          this.model.unsettled_introductory_financial_items.forEach(item => this.selectedItems.add(item));
        }
        this.selectedItems = new Set(this.selectedItems);
      },
      clearSelected() {
        this.selectedItems.clear();
        this.selectedItems = new Set([...this.selectedItems]);
        this.isSelectedAll = false;
      },
      onSettledRowSelect(checked, item) {
        if (checked) {
          this.selectedSettledItems.add(item)
        } else {
          this.selectedSettledItems.delete(item)
        }
        this.isAllSettledSelected = this.selectedSettledItems.size === this.model.settled_introductory_financial_items.length;
        this.selectedSettledItems = new Set(this.selectedSettledItems);
      },
      selectAllSettledRows(checked) {
        this.clearSettledSelected();
        this.isAllSettledSelected = checked;

        if (this.isAllSettledSelected) {
          this.model.settled_introductory_financial_items.forEach(item => this.selectedSettledItems.add(item));
        }
        this.selectedSettledItems = new Set(this.selectedSettledItems);
      },
      clearSettledSelected() {
        this.selectedSettledItems.clear();
        this.selectedSettledItems = new Set([...this.selectedSettledItems]);
        this.isAllSettledSelected = false;
      },
      async onSubmit() {
        if (this.model.is_not_editable) {
          this.$errorDialog(this.$i18n.t('You can not edit legacy settlement'));
          return;
        }
        const items = [...this.model.settled_introductory_financial_items, ...this.model.unsettled_introductory_financial_items]
        for (let item of items) {
          if (item.on_hold && item.on_hold !== this.onHoldInitial[item.uuid] && item.settlement && item.settlement.status === 'paid') {
            const confirm = await this.$confirm(this.$t('The associated {document_type} is Paid. Do you want to mark Item as On Hold?', {document_type: this.isAuction ? this.$t('Auction Settlement') : this.$t('Manual Settlement')}))
            if (confirm) {
              break
            } else {
              return
            }
          }
        }
        this.loading = true;
        let response;

        let requestData = {
          uuid: this.model.uuid,
          type: this.isManual ? 'manual_introductory' : 'auction_introductory',
          auction_uuid: this.isManual ? null : (this.model.auction_uuid ? this.model.auction_uuid : this.computedSale.uuid),
          additional_page: this.model.additional_page,
          customer_uuid: this.currentCustomer.uuid,
          preferred_language_code: this.model.preferred_language_code,
          introductory_financial_items: this.model.settled_introductory_financial_items,
          miscellaneous_item_uuids: [],
          date: this.model.date,
          unsettled_introductory_financial_items: this.model.unsettled_introductory_financial_items,
          settlement_billing_address: this.model.settlement_billing_address,
        };

        if (this.modal.debitNoteWithoutRefundParams) {
          requestData.inherited_from_uuid = requestData.uuid;
          requestData.uuid = null;
          requestData.debit_note_without_refund_params = this.modal.debitNoteWithoutRefundParams;
          response = await SettlementService.createSettlement(requestData);
        } else if (!requestData.uuid) {
          response = await SettlementService.createSettlement(requestData);
        } else {
          response = await SettlementService.updateSettlement(requestData.uuid, requestData);
        }

        this.loading = false;

        if (response.success) {
          this.showConfirmation = false;
          if (this.modal.source === SOURCE_SETTLEMENTS_TABLE) {
            this.getTableData();
            this.hideIntroductorySettlementModal();
          }
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          }
        }
      },
      async onCustomerChange(customer) {
        this.currentCustomer = _.cloneDeep(customer);
        this.model.preferred_language_code = this.currentCustomer.preferred_language_code
        this.customerUuid = customer.uuid
        if (customer.billing_address) {
          this.setDefaultAddresses(
            customer.billing_address,
            null
          )
        } else {
          this.model.settlement_billing_address = null
        }

        await this.getData()
      },
      async onSaleChange(sale) {
        this.currentSale = sale
        await this.getData()
      },
      onCustomerUnselect() {
        this.currentCustomer = null;
        this.model.unsettled_introductory_financial_items = [];
        this.model.settled_introductory_financial_items = [];
      },
      onSaleUnselect() {
        this.currentSale = null;
        this.model.unsettled_introductory_financial_items = [];
        this.model.settled_introductory_financial_items = [];
      },
      async getData() {
        if (!this.currentCustomer) {
          return
        }

        if (this.isAuction && !this.computedSale) {
          return
        }

        let response;

        if (this.isAuction) {
          response = await SettlementService.getUnsettledFinancialItemsByCustomerForAuction(this.currentCustomer.uuid, this.computedSale.uuid, 'lot.inventory,settlement')
        } else {
          response = await SettlementService.getUnsettledFinancialItemsByCustomerForManual(this.currentCustomer.uuid, 'inventory,settlement')
        }

        if (response.success) {
          this.model.unsettled_introductory_financial_items = response.body.results
            .filter(i => i.introductory_commission > 0 && !i.introductory_settlement_uuid)
            .map(i => {
              let model = new FinancialItemModel(i)
              this.calcTaxForItem(model)
              return model
            });
          this.model.unsettled_introductory_financial_items.forEach(item => {
            this.onHoldInitial[item.uuid] = item.on_hold
          })
        }
      },
      clearData() {
        this.model.settled_introductory_financial_items = [];
        this.model.unsettled_introductory_financial_items = [];
        this.isSelectedAll = false;
        this.selectedItems = new Set();
        this.isAllSettledSelected = false;
        this.selectedSettledItems = new Set();
        this.currentCustomer = null;
        this.currentSale = null
        this.onHoldInitial = {};
      },
      onTaxableChange(val, item) {
        this.$nextTick(() => {
          this.calcTaxForItem(item)
        })
      },
      onCommissionChange(item) {
        this.$nextTick(() => {
          this.calcTaxForItem(item)
        })
      },
      calcTaxForItem(item) {
        if (!item.introductory_commission_taxable) {
          item.introductory_commission_vat = 0;
        } else {
          item.introductory_commission_vat = parseFloat(item.introductory_commission * (this.currentBranch.tax_rate / 100)).toFixed(2)
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .settlement-wrapper {
    white-space: nowrap;
  }

  .unsettled-items-table, .settled-items-table {
    .lot-col {
    }

    .receipt-col {
    }

    .hammer-price-col {
      white-space: break-spaces;
    }

    .hammer-adjustment-col {
      white-space: break-spaces;
    }

  }

  .on-hold-notif {
    position: absolute;
    left: 0;
    bottom: 0;
    border: none;
    color: red;
    font-size: 11px;
    margin-top: 30px;
  }


</style>
